import React from 'react';
import { Phone, Mail, Clock, Facebook } from 'lucide-react';
import { businessInfo } from '../mock';
import { useTranslation, Trans } from 'react-i18next';

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useTranslation();

  const quickLinks = [
    { label: t('nav.services'), href: '#services' },
    { label: t('nav.about'), href: '#about' },
    { label: t('nav.reviews'), href: '#reviews' },
    { label: t('nav.contact'), href: '#contact' }
  ];

  return (
    <footer id="contact" className="bg-neutral-950 border-t border-neutral-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <img
                src="https://customer-assets.emergentagent.com/job_kamotors/artifacts/1wvd6bp4_logg.jpeg"
                alt="K&A Motors Tech Solutions"
                className="h-16 w-16 object-contain"
              />
              <div>
                <h3 className="text-xl font-bold text-white">{businessInfo.name}</h3>
                <p className="text-xs text-lime-400">{businessInfo.tagline}</p>
              </div>
            </div>
            <p className="text-neutral-400 text-sm leading-relaxed">
              {t('footer_desc')} 
            </p>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h4 className="text-lg font-bold text-white">Contact Us</h4>
            <div className="space-y-3">
             <a
                href={`https://wa.me/${businessInfo.whatsapp.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start space-x-3 text-neutral-300 hover:text-lime-400 transition-colors group"
              >
                <svg className="h-5 w-5 mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                <span>WhatsApp</span>
              </a>
              <a
                href={`mailto:${businessInfo.email}`}
                className="flex items-start space-x-3 text-neutral-300 hover:text-lime-400 transition-colors group"
              >
                <Mail className="h-5 w-5 mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform" />
                <span>{businessInfo.email}</span>
              </a>

              <a
                href={businessInfo.facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start space-x-3 text-neutral-300 hover:text-lime-400 transition-colors group"
              >
                <Facebook className="h-5 w-5 mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform" />
                <span>{t('footer_follow')}</span>
              </a>
              
            </div>
          </div>

          {/* Business Hours */}
          <div className="space-y-4">
            <h4 className="text-lg font-bold text-white flex items-center space-x-2">
              <Clock className="h-5 w-5" />
              <span>{t('footer_hours')}</span>
            </h4>
            <div className="space-y-2 text-neutral-300">
              <p>{t('hours.weekdays')}</p>
              <p className="text-neutral-500">{t('hours.saturday')}</p>
              <p className="text-neutral-500">{t('hours.sunday')}</p>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="text-lg font-bold text-white">{t('footer_links')}</h4>
            <nav className="flex flex-col space-y-2">
              {[
                { label: 'Services', href: '#services' },
                { label: 'About Us', href: '#about' },
                { label: 'Reviews', href: '#reviews' },
                { label: 'Contact', href: '#contact' }
              ].map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="text-neutral-300 hover:text-lime-400 transition-colors"
                >
                  {link.label}
                </a>
              ))}
              <a
                href={businessInfo.squareSite}
                target="_blank"
                rel="noopener noreferrer"
                className="text-neutral-300 hover:text-lime-400 transition-colors"
              >
                Square Site
              </a>
              <a
                href="https://junk-snow.kamotorstechsolutions.site/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-neutral-300 hover:text-lime-400 transition-colors"
              >
                Junk and Snow site
              </a>
            </nav>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-neutral-800 text-center">
          <p className="text-neutral-400 text-sm">
            © {currentYear} {businessInfo.name}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};
